import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let authToken = null;
export const setAuthToken = (token) => { authToken = token; };

const api = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });
api.interceptors.request.use((config) => {
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  return config;
});

// Providers
export const createProvider = (data) => api.post("/providers", data);
export const listProviders = () => api.get("/providers");
export const deleteProvider = (id) => api.delete(`/providers/${id}`);

// Chats
export const createChat = (data) => api.post("/chats", data);
export const listChats = () => api.get("/chats");
export const getChat = (id) => api.get(`/chats/${id}`);
export const deleteChat = (id) => api.delete(`/chats/${id}`);
export const getChatMessages = (chatId) => api.get(`/chats/${chatId}/messages`);

// Messages (SSE streaming)
export async function sendMessageStream(chatId, message, options, onChunk, onMeta, onDone, onError, onWebSearch) {
  const url = `${API}/chats/${chatId}/message/stream`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const response = await fetch(url, {
    method: "POST", headers,
    body: JSON.stringify({ message, image_base64: options.image_base64 || null, web_search: options.web_search || false, deep_reasoning: options.deep_reasoning || false }),
  });
  if (!response.ok) {
    let errMsg = `Request failed (${response.status})`;
    try { errMsg = await response.text(); } catch { /* body may already be consumed */ }
    throw new Error(errMsg);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === "chunk") onChunk(data.content);
          else if (data.type === "meta") onMeta(data);
          else if (data.type === "done") onDone(data);
          else if (data.type === "error") onError(data.detail);
          else if (data.type === "web_search" && onWebSearch) onWebSearch(data.context);
        } catch {}
      }
    }
  }
}

// Marketplace
export const browseCapsules = (category) => api.get("/marketplace/capsules", { params: category ? { category } : {} });
export const getCapsule = (id) => api.get(`/marketplace/capsules/${id}`);
export const publishCapsule = (data) => api.post("/marketplace/publish", data);
export const publishAgentFromChat = (data) => api.post("/marketplace/publish-agent", data);
export const buyPass = (capId, originUrl) => api.post(`/marketplace/${capId}/buy-pass`, { origin_url: originUrl });
export const previewPay = (capId, originUrl) => api.post(`/marketplace/${capId}/preview-pay`, { origin_url: originUrl });
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getMyPasses = () => api.get("/marketplace/my-passes");
export const capsuleQuery = (capId, message) => api.post(`/marketplace/${capId}/query`, { message });
export const previewQuery = (capId, message) => api.post(`/marketplace/${capId}/preview-query`, { message });
export const getCapsuleReviews = (capId) => api.get(`/marketplace/${capId}/reviews`);
export const createCapsuleReview = (capId, data) => api.post(`/marketplace/${capId}/reviews`, data);
export const getCreatorEarnings = () => api.get("/creator/earnings");
export const getMyAgentNetwork = () => api.get("/agent/my-network");
export const buyAgentAccess = (agentId, originUrl) => api.post(`/agent/${agentId}/buy-access`, { origin_url: originUrl });
export const checkAgentAccess = (agentId) => api.get(`/agent/${agentId}/access-status`);
export const adminCheck = () => api.get("/admin/check");
export const adminDeleteAgent = (agentId) => api.delete(`/admin/agents/${agentId}`);
export const authMe = () => api.get("/auth/me");

// Agent Internet
export const getAgentHome = () => api.get("/agent/home");
export const searchAgents = (q) => api.get("/agent/search", { params: { q } });
export const getAgent = (id) => api.get(`/agent/${id}`);
export const getRelatedAgents = (id) => api.get(`/agent/${id}/related`);
export const getAgentRecommendations = (id) => api.get(`/agent/${id}/recommendations`);
export const getAgentNetwork = () => api.get("/agent/network");
export const routerQuery = (prompt) => api.post("/agent/router/query", { prompt });
export const routerDebug = (requestId) => api.get(`/agent/router/debug/${requestId}`);
export const getAgentHistory = (id) => api.get(`/agent/${id}/history`);
export const agentChat = (id, message, options = {}) =>
  api.post(`/agent/${id}/chat`, { message, ...options });

export async function agentChatStream(agentId, message, options, onChunk, onMeta, onDone, onError) {
  const url = `${API}/agent/${agentId}/chat/stream`;
  const headers = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const response = await fetch(url, {
    method: "POST", headers,
    body: JSON.stringify({ message, web_search: options.web_search || false, deep_reasoning: options.deep_reasoning || false }),
  });
  if (!response.ok) {
    let errMsg = `Request failed (${response.status})`;
    try { errMsg = await response.text(); } catch { /* body may already be consumed */ }
    throw new Error(errMsg);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === "chunk") onChunk(data.content);
          else if (data.type === "meta") onMeta(data);
          else if (data.type === "done") onDone(data);
          else if (data.type === "error") onError(data.detail);
        } catch {}
      }
    }
  }
}

export default api;
